import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

function Catalogue() {
  const [activeTab, setActiveTab] = useState('nanomaterials');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchProducts(activeTab);
  }, [activeTab]);
  
  const fetchProducts = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${category}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-950 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400" data-testid="catalogue-title">
            Product Catalogue
          </h1>
          <p className="text-xl text-gray-400">
            Explore our comprehensive range of advanced materials
          </p>
        </div>
        
        {/* Category Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-slate-800/50 backdrop-blur-sm rounded-xl p-1 border border-slate-700">
            <button
              onClick={() => setActiveTab('nanomaterials')}
              data-testid="tab-nanomaterials"
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'nanomaterials'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Nanomaterials
            </button>
            <button
              onClick={() => setActiveTab('battery-materials')}
              data-testid="tab-battery-materials"
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'battery-materials'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Battery Materials
            </button>
          </div>
        </div>
        
        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-slate-700 hover:border-cyan-500/50 transition-all group"
                data-testid={`product-${product.id}`}
              >
                <div className="h-56 overflow-hidden">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-cyan-400 uppercase px-3 py-1 bg-cyan-500/10 rounded-full">
                      {activeTab === 'nanomaterials' ? 'Nanomaterials' : 'Battery Materials'}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{product.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                  {product.specifications && (
                    <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
                      <p className="text-xs text-gray-500 mb-1 font-semibold">Specifications:</p>
                      <p className="text-xs text-gray-400">{product.specifications}</p>
                    </div>
                  )}
                  <Link 
                    to="/contact"
                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all block text-center"
                    data-testid={`inquire-button-${product.id}`}
                  >
                    Request Quote
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-xl">No products found in this category.</p>
          </div>
        )}
        
        {/* Contact CTA */}
        <div className="mt-20 bg-gradient-to-r from-blue-900/20 via-cyan-900/20 to-blue-900/20 rounded-2xl p-12 text-center border border-cyan-500/20">
          <h2 className="text-3xl font-bold text-white mb-4">Need a Custom Solution?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            We offer custom formulations and specifications tailored to your specific requirements
          </p>
          <a
            href="/contact"
            data-testid="custom-solution-button"
            className="inline-block px-8 py-4 bg-white text-blue-900 font-bold rounded-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
          >
            Contact Our Experts
          </a>
        </div>
      </div>
    </div>
  );
}

export default Catalogue;